import { useEffect, useMemo, useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { dfLocale as fr } from "../lib/i18n";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle, Trash2, Tablet } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/labels";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, Field } from "../components/Shared";
import { api, formatApiError } from "../lib/api";
import { MOVE_STATUS, STAFF_ROLES } from "../lib/labels";

const EMPTY = { title: "", client_name: "", date: "", start_time: "08:00", origin: "", destination: "", volume_m3: 0, staff_ids: [], vehicle_ids: [], status: "planifie", notes: "" };

function CheckList({ items, selected, onChange, label, testid }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Field label={label}>
      <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid={testid}>
        {items.map((it) => (
          <label key={it.id} className="flex items-center gap-2 text-sm">
            <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`${testid}-${it.id}`} />
            {it.name} <span className="text-xs text-slate-400">{it.sub}</span>
          </label>
        ))}
        {items.length === 0 && <p className="text-xs text-slate-400">Aucun élément</p>}
      </div>
    </Field>
  );
}

function MoveDialog({ open, initial, onClose, onSaved, staff, vehicles }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    setForm({ ...EMPTY, ...initial });
  }, [initial, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? (k === "volume_m3" ? Number(e.target.value) : e.target.value) : e });
  const save = async () => {
    try {
      const { id, company_id, created_at, conflicts, ...p } = form;
      if (id) await api.put(`/moves/${id}`, p);
      else await api.post("/moves", p);
      toast.success("Déménagement enregistré");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    await api.delete(`/moves/${form.id}`);
    toast.success("Supprimé");
    onSaved();
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="move-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Modifier le déménagement" : "Planifier un déménagement"}</DialogTitle></DialogHeader>
        {form.conflicts?.length > 0 && (
          <p className="flex items-center gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="move-conflict-warning"><AlertTriangle className="h-4 w-4" />Conflit de ressources avec : {form.conflicts.join(", ")}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Intitulé"><Input data-testid="move-title-input" value={form.title} onChange={set("title")} /></Field></div>
          <Field label="Client"><Input data-testid="move-client-input" value={form.client_name} onChange={set("client_name")} /></Field>
          <Field label="Statut">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="move-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MOVE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date"><Input data-testid="move-date-input" type="date" value={form.date} onChange={set("date")} /></Field>
          <Field label="Heure"><Input data-testid="move-time-input" type="time" value={form.start_time} onChange={set("start_time")} /></Field>
          <Field label="Départ"><Input data-testid="move-origin-input" value={form.origin} onChange={set("origin")} /></Field>
          <Field label="Arrivée"><Input data-testid="move-destination-input" value={form.destination} onChange={set("destination")} /></Field>
          <Field label="Volume (m³)"><Input data-testid="move-volume-input" type="number" min="0" value={form.volume_m3} onChange={set("volume_m3")} /></Field>
          <div />
          <CheckList label="Équipe" testid="move-staff" items={staff.map((s) => ({ id: s.id, name: s.name, sub: STAFF_ROLES[s.role] }))} selected={form.staff_ids} onChange={set("staff_ids")} />
          <CheckList label="Véhicules" testid="move-vehicles" items={vehicles.map((v) => ({ id: v.id, name: v.name, sub: v.plate }))} selected={form.vehicle_ids} onChange={set("vehicle_ids")} />
        </div>
        <DialogFooter className="gap-2">
          {form.id && can(user, "field") && <Button asChild variant="outline" data-testid="move-field-report-link"><Link to={`/nomade/chantier/${form.id}`}><Tablet className="mr-2 h-4 w-4" />Suivi terrain</Link></Button>}
          {form.id && <Button variant="outline" data-testid="move-delete-button" onClick={remove}><Trash2 className="mr-2 h-4 w-4 text-red-500" />Supprimer</Button>}
          <Button data-testid="move-save-button" className="bg-[#0b2c6b]" onClick={save}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Planning() {
  const [month, setMonth] = useState(new Date());
  const [moves, setMoves] = useState([]);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/moves").then((r) => setMoves(r.data));
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) }), [month]);

  return (
    <div className="fade-up">
      <PageHeader overline="Opérations" title="Planning des déménagements" subtitle="Affectez équipes et véhicules, les conflits sont signalés automatiquement.">
        <Button data-testid="add-move-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]" onClick={() => setEditing({ date: format(new Date(), "yyyy-MM-dd") })}><Plus className="mr-2 h-4 w-4" />Planifier</Button>
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-4 flex items-center justify-between">
          <Button size="icon" variant="ghost" data-testid="prev-month-button" onClick={() => setMonth(addMonths(month, -1))}><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="text-lg font-semibold capitalize text-slate-900" data-testid="planning-month-label">{format(month, "MMMM yyyy", { locale: fr })}</h2>
          <Button size="icon" variant="ghost" data-testid="next-month-button" onClick={() => setMonth(addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg bg-slate-200 text-xs" data-testid="planning-calendar">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d} className="bg-slate-50 p-2 text-center font-semibold text-slate-500">{d}</div>)}
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayMoves = moves.filter((m) => m.date === key);
            return (
              <div key={key} data-testid={`day-${key}`} onClick={() => setEditing({ date: key })}
                className={`min-h-[96px] cursor-pointer bg-white p-1.5 transition-colors hover:bg-blue-50/50 ${isSameMonth(day, month) ? "" : "opacity-40"}`}>
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${isToday(day) ? "bg-[#0b2c6b] font-bold text-white" : "text-slate-600"}`}>{format(day, "d")}</span>
                <div className="mt-1 space-y-1">
                  {dayMoves.map((m) => (
                    <button key={m.id} data-testid={`move-chip-${m.id}`} onClick={(e) => { e.stopPropagation(); setEditing(m); }}
                      className={`flex w-full items-center gap-1 truncate rounded px-1.5 py-1 text-left text-[11px] font-medium ${MOVE_STATUS[m.status]?.cls}`}>
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${MOVE_STATUS[m.status]?.dot}`} />
                      {m.conflicts?.length > 0 && <AlertTriangle className="h-3 w-3 shrink-0 text-amber-600" />}
                      <span className="truncate">{m.start_time} {m.title || m.client_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <MoveDialog open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={load} staff={staff} vehicles={vehicles} />
    </div>
  );
}
